import { useStore } from "@/store/store";
import type { TUserDataSchema, TUserRoleGroup } from "@/api/user";

type TRoleGroup = TUserRoleGroup | TUserRoleGroup[] | null | undefined;
type TUserLike = TUserDataSchema | null | undefined;

export const getPermissionsFromRoleGroup = (
  roleGroup: TRoleGroup,
): string[] => {
  if (!roleGroup) return [];

  if (Array.isArray(roleGroup)) {
    const flattened = roleGroup?.flatMap((role) => role.permissions ?? []);
    return flattened?.filter(
      (perm): perm is string => typeof perm === "string",
    );
  }

  return Array.isArray(roleGroup.permissions) ? roleGroup.permissions : [];
};

// Staff and superuser have full-access; no role-name check.
export const isAdminUser = (user: TUserLike): boolean => {
  if (!user) return false;
  return user.is_superuser === true || user.is_staff === true;
};

/**
 * Hook for permission-aware UI logic.
 * - Computes the flattened permission list for the effective role.
 * - Exposes helpers that automatically bypass checks for admin users.
 */
export const usePermissions = () => {
  const user = useStore((state) => state.user) as TUserLike;

  const roleGroup = user?.role_group as TRoleGroup;
  const userPermissions = getPermissionsFromRoleGroup(roleGroup);
  const isAdmin = isAdminUser(user);
  const isAgent = Array.isArray(roleGroup)
    ? roleGroup.some((role) => role.slug === "agent")
    : roleGroup?.slug === "agent";
  const hasPermission = (permission: string): boolean => {
    if (isAdmin) return true;
    return userPermissions.includes(permission);
  };

  // requireAll = true → AND logic. true only if user has BOTH permission
  // requireAll = false (default) → OR logic. true if user has any permission
  const hasAnyPermission = (
    permissions?: string[],
    requireAll = false,
  ): boolean => {
    if (isAdmin) return true;
    if (!permissions?.length) return false; // changed from true → false

    return requireAll
      ? permissions.every((p) => userPermissions?.includes(p))
      : permissions.some((p) => userPermissions?.includes(p));
  };

  return { hasPermission, hasAnyPermission, isAgent };
};
